import { useEffect, useState, useMemo, useCallback } from "react";
import { useHistory } from 'react-router-dom';
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import DetailsContainer from "../components/DetailsContainer";
import Header from "../components/navigationHeader/Header";
import { useParams } from "react-router-dom";
import DomainDetails from "../components/domain/DomainDetails";
import SiteMonitoring from "../components/domain/SiteMonitoring";
import ObjectList from "../components/ObjectList";
import LandingPage from "../components/LandingPage";
import HomeLandingPage from "./components/LandingPage";
import ChangeDomain from "../components/domain/ChangeDomain";
import { useGetDomain, useGetUser } from "../common/Hooks";
import { RolesProvider } from "../common/context/Roles";
import User from "../services/user";
import Auth from '../services/auth';
import Product from "../services/product";
import Utils from "../utils/utils";
import DeploymentManagement from "../components/deployment/DeploymentManagement";
import RuntimeConfigManagement from "../components/deployment/RuntimeConfigManagement";
import BuildTimeConfigManagement from "../components/deployment/buildTimeConfig/BuildTimeConfigManagement";
import FormsManagement from "../components/deployment/forms/FormsManagement";
import TermsAndConditionsModal from "../components/controls/TermsAndConditionsModal";
import NotificationsTab from "../components/notifications/NotificationsTab";

const Home = () => {
    const { objectType, id } = useParams();
    const [translate] = useTranslation();
    const [hasDomain, setHasDomain] = useState(!!Utils.getDomain());
    const [initialized, setInitialized] = useState(false);
    const [userHasNoPermissions, setUserHasNoPermissions] = useState(true);
    const [{ _id: domainId, acceptedTermsAndConditionsUrl, termsAndConditionsUrl }, setDomain] = useGetDomain();
    const [user, setUser] = useGetUser({});

    const [showTC, setShowTC] = useState(false);
    const history = useHistory();

    const userIsFormsOnly = (user) => {
        if (user.isGuest || user.appAdmin || user.adminForDomains?.length > 0 || user.domain?.length > 1 || user.singleDomainUser === false) {
            return false;
        }
        let foundPermissions = false;

        user.uiPermissions.forEach((uiPermission) => {
            if (["forms", "formsInstance", "formsCompleter"].includes(uiPermission.type)) {
                foundPermissions = true;
            }
        });

        return foundPermissions;
    }

    const checkPermissions = (userObject) => userObject.appAdmin || userObject.adminForDomains.length > 0 || userObject.uiPermissions.length > 0;

    const initialize = useCallback(async (user) => {
        if (user._id) {
            setInitialized(true);
            setUserHasNoPermissions(!checkPermissions(user));
            return;
        }

        const userDetails = await User.getUser();
        if (!userDetails.error) {
            const loggedUser = userDetails.data;

            const hasPermissions = checkPermissions(loggedUser);
            setUserHasNoPermissions(!hasPermissions);

            if (hasPermissions) {
                if (loggedUser.singleDomainUser) {
                    const promptInfoResponse = await Product.getDomainPromptInfo(loggedUser.domain[0]._id);
                    const { helpUrl, termsAndConditionsUrl } = promptInfoResponse.data;
                    setDomain({
                        _id: loggedUser.domain[0]._id,
                        name: loggedUser.domain[0].name,
                        helpUrl,
                        termsAndConditionsUrl,
                        acceptedTermsAndConditionsUrl: loggedUser.domain[0].acceptedTermsAndConditionsUrl,
                    });
                } else {
                    setHasDomain(false);
                }
                window.sessionStorage.setItem(
                    "root-domain",
                    loggedUser.domain[0].rootDomain || false
                );
                setUser({
                    _id: loggedUser._id,
                    email: loggedUser.email,
                    appAdmin: loggedUser.appAdmin,
                    firstName: loggedUser.firstName,
                    lastName: loggedUser.lastName,
                    adminForDomains: loggedUser.adminForDomains,
                    uiPermissions: loggedUser.uiPermissions,
                    singleDomainUser: loggedUser.singleDomainUser
                });

                if (userIsFormsOnly(loggedUser)) {
                    window.sessionStorage.setItem("formsUser", "true");
                    history.push("/forms");
                }
            }

            setInitialized(true);
        }
    }, [setDomain, setUser, history, setInitialized, setHasDomain]);

    useEffect(() => {
        if (!initialized) {
            initialize(user);
        }
    }, [initialize, initialized, user]);

    useEffect(() => {
        if (domainId) {
            setHasDomain(true);
        }
    }, [domainId, setHasDomain]);

    useEffect(() => {
        if (user.singleDomainUser && !acceptedTermsAndConditionsUrl && user.adminForDomains.includes(domainId) && termsAndConditionsUrl) {
            setShowTC(true);
        }
    }, [user, acceptedTermsAndConditionsUrl, termsAndConditionsUrl, domainId]);

    const handleLogout = useCallback(async () => {
        await Auth.logout();
        history.push("/auth");
    }, [history]);

    const fullPage = useMemo(() => {
        return {
            "undefined": <LandingPage />,
            "domain-settings": <DomainDetails id={domainId} />,
            "deployment-management": <DeploymentManagement />,
            "runtime-config": <RuntimeConfigManagement />,
            "build-time-config": <BuildTimeConfigManagement />,
            "forms-config": <FormsManagement />,
            "notifications": <NotificationsTab />,
            "site-monitoring": <SiteMonitoring />,
        }
    }, [domainId])

    const renderSplitPage = useMemo(() => (
        <div className="d-flex flex-row overflow-hidden">
            <div className="d-none d-lg-block col-12 col-lg-4 mt-3 overflow-auto">
                <ObjectList />
            </div>
            <div className="px-3 col-12 col-lg-8 mt-3 d-flex flex-column overflow-hidden">
                {id && <DetailsContainer />}
            </div>
        </div>
    ), [id]);

    const renderFullPage = useCallback((displayKey) => {
        return fullPage[displayKey]
    }, [fullPage]);

    const render = useMemo(() => {
        const displayKey = objectType === undefined ? "undefined" : objectType;
        if (Object.keys(fullPage).includes(displayKey)) {
            return renderFullPage(displayKey)
        }

        return renderSplitPage;

    }, [objectType, fullPage, renderSplitPage, renderFullPage]);

    if (!initialized) {
        return <div className="bg-white h-100"></div>
    }

    if (userHasNoPermissions) {
        return (
            <HomeLandingPage>
                <div className="d-flex flex-column text-center">
                    <span>{translate('OTM_USER_HAS_NO_PERMISSIONS')}</span>
                    <div>
                        <Button
                            variant="primary"
                            className="mt-2"
                            onClick={handleLogout}
                        >
                            {translate('OTM_LOGOUT')}
                        </Button>
                    </div>
                </div>
            </HomeLandingPage>
        );
    }

    if (!hasDomain) {
        return <ChangeDomain show={true} cancelText="OTM_LOGOUT" onCancel={handleLogout} />
    }

    return (
        <>
            <TermsAndConditionsModal
                show={showTC}
                onConfirm={() => { setShowTC(false) }}
            />
            <Header />
            <main className="container-fluid flex-grow-1 d-flex flex-column overflow-auto bg-light">
                <RolesProvider deploymentId={id}>
                    {render}
                </RolesProvider>
            </main>
        </>
    );
};

export default Home;